import api, { DebugSessionInformationJsonData, DebugSessionStatusJsonData } from './MeshApi';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from './AuthService';

const fetchLimit = 25;
const promptMaxLength = 4096;

const statusClasses: Record<DebugSessionStatusJsonData, string> = {
  Pending: 'pending',
  Running: 'building',
  Complete: 'success',
  Failed: 'failed',
};

const DebugSessions = () => {
  const [data, setData] = useState<DebugSessionInformationJsonData[] | null>(null);
  const [hasPrevious, setHasPrevious] = useState(true);
  const [hasNext, setHasNext] = useState(true);
  const [offset, setOffset] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const navigate = useNavigate();

  const handleCreate = async () => {
    try {
      const response = await api.createDebugSession(prompt);
      setPrompt('');
      setOffset(0);
      setReloadKey(reloadKey + 1);
      alert(`Debug session queued: ${response.session_id}`);
    } catch (error) {
      alert(`An error occurred while creating the debug session: ${error}`);
    }
  };

  const handleAbandon = async (session: DebugSessionInformationJsonData) => {
    const confirmation = window.confirm(`Are you sure you want to mark session ${session.session_id} failed?`);
    if (confirmation) {
      try {
        await api.updateDebugSessionStatus(session.session_id, 'Failed');
        setReloadKey(reloadKey + 1);
      } catch (error) {
        alert(`An error occurred while updating the debug session: ${error}`);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (offset > fetchLimit) {
      setOffset(offset - fetchLimit);
    } else if (offset > 0) {
      setOffset(0);
    }
  };

  const handleNextPage = async () => {
    if (data) {
      setOffset(offset + data.length);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await api.getDebugSessions(offset, fetchLimit);
        setData(data);
        setHasPrevious(offset > 0);
        setHasNext(data.length === fetchLimit);
      } catch (error) {
        console.log('Failed to fetch debug sessions:', error);
        authService.logout();
        navigate('/');
      }
    };

    fetchData();
  }, [offset, reloadKey, navigate]);

  if (data == null) {
    return <div className="spinner"></div>;
  }
  return (
    <div className="container mt-4">
      <div className="form-container">
        <div className="form-group">
          <label htmlFor="prompt">Prompt:</label>
          <textarea
            id="prompt"
            name="prompt"
            className="form-input"
            rows={3}
            maxLength={promptMaxLength}
            value={prompt}
            onChange={event => setPrompt(event.target.value)}
          />
          <button disabled={!prompt} type="button" className="form-button" onClick={handleCreate}>
            Start Session
          </button>
        </div>
      </div>
      <table className="builds-table">
        <thead>
          <tr>
            <th>Session</th>
            <th>Status</th>
            <th>Created</th>
            <th>Started</th>
            <th>Last Status Update</th>
            <th>Last Status Message</th>
            <th>Prompt</th>
            <th>Issue</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(session => (
            <tr key={session.session_id}>
              <td className="text-break">{session.session_id}</td>
              <td>
                <span className={`status-badge ${statusClasses[session.status]}`}>{session.status}</span>
              </td>
              <td>{session.create_time ? new Date(session.create_time).toLocaleString() : ''}</td>
              <td>{session.start_time ? new Date(session.start_time).toLocaleString() : ''}</td>
              <td>{session.last_status_update ? new Date(session.last_status_update).toLocaleString() : ''}</td>
              <td>{session.last_status_message || ''}</td>
              <td className="text-break">{session.prompt || ''}</td>
              <td className="text-break">
                {session.issue_url ? (
                  <a href={session.issue_url} target="_blank" rel="noreferrer">
                    {session.issue_url}
                  </a>
                ) : (
                  ''
                )}
              </td>
              <td>
                {(session.status === 'Pending' || session.status === 'Running') && (
                  <button className="retry-btn" onClick={() => handleAbandon(session)}>
                    Mark Failed
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination-controls">
        <button className="pagination-btn" onClick={handlePreviousPage} disabled={!hasPrevious}>
          Previous
        </button>
        <button className="pagination-btn" onClick={handleNextPage} disabled={!hasNext}>
          Next
        </button>
      </div>
    </div>
  );
};

export default DebugSessions;
